import { useCallback, useEffect, useRef, useState } from "react";

// Painel sysadmin dos motores de IA (Claude e Codex) com MULTI-CONTA:
// cada motor tem N contas (perfis de credencial). O uso é em RODÍZIO
// balanceado; conta que estoura limite/erro entra em cooldown e o sistema
// alterna sozinho (failover) - ver src/engineProfiles.ts no backend.
// O login de cada conta usa os fluxos guiados (ver src/engineAuth.ts).

type Engine = "claude" | "codex" | "gemini";

interface ApprovalPrompt {
  text: string;
  options: string[];
  selectedIndex: number;
}

interface ActiveLogin {
  engine: Engine;
  profileId: string;
  status:
    | "starting"
    | "awaiting-code"
    | "awaiting-authorization"
    | "awaiting-approval"
    | "finishing"
    | "done"
    | "error"
    | "canceled";
  url?: string;
  userCode?: string;
  error?: string;
  /** Prompt de decisão do CLI (ex.: variáveis OTEL da organização) - o sysadmin escolhe. */
  prompt?: ApprovalPrompt;
  /** Saída bruta do CLI (sem ANSI) - diagnóstico pro sysadmin. */
  output?: string;
}

interface Profile {
  id: string;
  label: string;
  authed: boolean;
  cooling: boolean;
  cooldownUntil?: string | null;
  cooldownReason?: string | null;
  lastUsedAt?: string | null;
  /** Uso registrado por este sistema no mês corrente. */
  usage?: { month: string; responses: number; inputTokens: number; outputTokens: number; costUsd: number } | null;
}

type Strategy = "balanced" | "sequential" | "manual";

interface AuthState {
  status: { claude: boolean; codex: boolean };
  codexCliInstalled: boolean;
  geminiCliInstalled?: boolean;
  active: ActiveLogin | null;
  profiles: { claude: Profile[]; codex: Profile[]; gemini?: Profile[] };
  rotation: { claude: RotationCfg; codex: RotationCfg; gemini?: RotationCfg };
  /** Percentual de uso AO VIVO (API OAuth da Anthropic) por conta do Claude. */
  liveUsage?: Record<string, LiveUsage | null>;
}

interface RotationCfg {
  strategy: Strategy;
  activeProfileId: string | null;
}

interface LiveUsage {
  fiveHourPct: number | null;
  fiveHourResetsAt: string | null;
  sevenDayPct: number | null;
  sevenDayResetsAt: string | null;
}

const STRATEGY_LABEL: Record<Strategy, string> = {
  balanced: "Balanceado (rodízio entre as contas)",
  sequential: "Uma após a outra (só troca quando os tokens acabam)",
  manual: "Manual (eu escolho a conta ativa)",
};

const LABEL: Record<Engine, string> = { claude: "Claude", codex: "Codex (OpenAI)", gemini: "Gemini (Google)" };

function fmtTok(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function UsageBar({ label, pct, resetsAt }: { label: string; pct: number | null; resetsAt: string | null }) {
  if (pct == null) return null;
  const danger = pct >= 90;
  return (
    <div className="engine-usage-row" title={resetsAt ? `reseta ${new Date(resetsAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}` : ""}>
      <span className="engine-usage-label">{label}</span>
      <span className="engine-usage-track">
        <span className={`engine-usage-fill${danger ? " danger" : ""}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </span>
      <span className={`engine-usage-pct${danger ? " danger" : ""}`}>{Math.round(pct)}%</span>
      {resetsAt && <span className="admin-user-meta">reseta {fmtHour(resetsAt)}</span>}
    </div>
  );
}

function fmtHour(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

export function EngineAuthPanel() {
  const [state, setState] = useState<AuthState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState<Record<Engine, string>>({ claude: "", codex: "", gemini: "" });
  // Gemini: chave de API por conta (o Google DESATIVOU o OAuth individual do CLI)
  const [geminiKey, setGeminiKey] = useState<Record<string, string>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    const r = await fetch("/api/admin/engine-auth");
    if (r.ok) setState((await r.json()) as AuthState);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Enquanto há login em andamento, faz polling do estado.
  const active = state?.active;
  const activeOngoing =
    active &&
    (active.status === "starting" ||
      active.status === "awaiting-code" ||
      active.status === "awaiting-authorization" ||
      active.status === "awaiting-approval" ||
      active.status === "finishing");
  useEffect(() => {
    if (activeOngoing && !pollRef.current) {
      pollRef.current = setInterval(() => void load(), 2500);
    }
    if (!activeOngoing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [activeOngoing, load]);

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      setMsg(null);
      const r = await fetch(path, init ?? { method: "POST" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) setMsg((j as { error?: string }).error ?? "falha na operação");
      await load();
      return r.ok;
    },
    [load],
  );

  const start = useCallback(
    async (engine: Engine, profileId: string, mode?: "web") => {
      setBusy(`${engine}:${profileId}`);
      setCode("");
      try {
        const qs = new URLSearchParams({ profile: profileId });
        if (mode === "web") qs.set("mode", "web");
        await call(`/api/admin/engine-auth/${engine}/start?${qs.toString()}`);
      } finally {
        setBusy(null);
      }
    },
    [call],
  );

  const submitCode = useCallback(async () => {
    if (!code.trim()) return;
    const eng = active?.engine === "gemini" ? "gemini" : "claude";
    setBusy(`${eng}:code`);
    try {
      const ok = await call(`/api/admin/engine-auth/${eng}/code`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ code: code.trim() }),
      });
      if (ok) setCode("");
    } finally {
      setBusy(null);
    }
  }, [code, call]);

  const submitCodexUrl = useCallback(async () => {
    if (!code.trim()) return;
    setBusy("codex:url");
    try {
      const ok = await call("/api/admin/engine-auth/codex/callback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: code.trim() }),
      });
      if (ok) setCode("");
    } finally {
      setBusy(null);
    }
  }, [code, call]);

  const approve = useCallback(
    async (index: number) => {
      setBusy("claude:approve");
      try {
        await call("/api/admin/engine-auth/claude/approve", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ index }),
        });
      } finally {
        setBusy(null);
      }
    },
    [call],
  );

  const cancel = useCallback(async () => {
    await call("/api/admin/engine-auth/cancel");
  }, [call]);

  const addAccount = useCallback(
    async (engine: Engine) => {
      const label = newLabel[engine].trim();
      if (!label) return;
      const ok = await call(`/api/admin/engine-auth/${engine}/profiles`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ label }),
      });
      if (ok) setNewLabel((p) => ({ ...p, [engine]: "" }));
    },
    [call, newLabel],
  );

  if (!state) return <div className="admin-block">Carregando…</div>;

  const profileLabel = (engine: Engine, id: string): string =>
    (state.profiles[engine] ?? []).find((p) => p.id === id)?.label ?? id;

  const renderFlow = (engine: Engine) => {
    if (!active || active.engine !== engine || !activeOngoing) return null;
    const finishing = active.status === "finishing";

    if (active.status === "awaiting-approval" && active.prompt) {
      return (
        <div className="engine-flow">
          <p className="admin-sub" style={{ fontWeight: 600 }}>
            O Claude está pedindo uma decisão (configuração da sua organização) - escolha pra continuar:
          </p>
          <pre style={{ fontSize: 12, padding: "8px 10px", overflowX: "auto", background: "var(--bg-elevated)", borderRadius: 6, maxHeight: 260 }}>
            {active.prompt.text}
          </pre>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {active.prompt.options.map((opt, i) => (
              <button
                key={`${i}-${opt}`}
                className={i === active.prompt!.selectedIndex ? "btn-approve" : "btn-reject"}
                type="button"
                disabled={busy !== null}
                onClick={() => void approve(i)}
              >
                {opt.length > 60 ? `${opt.slice(0, 60)}…` : opt}
              </button>
            ))}
          </div>
          <button className="btn-reject" type="button" style={{ marginTop: 8 }} onClick={cancel}>
            Cancelar login
          </button>
        </div>
      );
    }

    if (active.status === "starting") {
      return (
        <div className="engine-flow">
          <p className="admin-sub" style={{ fontWeight: 600 }}>
            <span className="spinner-inline" aria-hidden /> Preparando o link de autenticação da conta
            "{profileLabel(engine, active.profileId)}"… (alguns segundos)
          </p>
          <button className="btn-reject" type="button" onClick={cancel}>Cancelar</button>
        </div>
      );
    }

    return (
      <div className="engine-flow">
        <p className="admin-sub" style={{ fontWeight: 600 }}>
          Autenticando a conta "{profileLabel(engine, active.profileId)}":
        </p>
        <ol>
          <li>
            Abra este link e entre na sua conta{engine === "claude" ? " Claude" : engine === "gemini" ? " Google" : " OpenAI"}:
            <br />
            <a href={active.url} target="_blank" rel="noreferrer">
              {active.url}
            </a>
          </li>
          {engine === "codex" && active.status === "awaiting-authorization" && (
            <li>
              Informe o código <code className="engine-code">{active.userCode}</code> e autorize - conclui{" "}
              <strong>sozinho</strong>.
              <span className="admin-user-meta" style={{ display: "block", marginTop: 6 }}>
                Se o site disser que a autenticação por código está desabilitada no workspace:
              </span>
              <button
                className="btn-reject"
                type="button"
                style={{ marginTop: 6 }}
                disabled={busy !== null}
                onClick={() => void start("codex", active.profileId, "web")}
              >
                Usar fluxo web (sem código)
              </button>
            </li>
          )}
          {engine === "codex" && active.status !== "awaiting-authorization" && (
            <li>
              Autorize. O navegador vai falhar em <code>localhost:1455</code> (esperado). Troque só o começo{" "}
              <code>http://localhost:1455</code> por <code className="engine-code">{window.location.origin}</code>{" "}
              <button className="btn-reject" type="button" onClick={() => void navigator.clipboard?.writeText(window.location.origin)}>
                Copiar
              </button>{" "}
              e dê Enter - conclui sozinho. Alternativa: cole a URL inteira aqui:
              {finishing ? (
                <p className="admin-sub" style={{ marginTop: 6 }}>Concluindo o login…</p>
              ) : (
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="http://localhost:1455/auth/callback?code=..."
                    className="engine-input"
                    style={{ flex: "1 1 320px", minWidth: 0 }}
                  />
                  <button className="btn-approve" type="button" disabled={busy !== null || !code.trim()} onClick={() => void submitCodexUrl()}>
                    Concluir
                  </button>
                </div>
              )}
              {active.status === "awaiting-code" && active.error && (
                <p className="auth-error" style={{ marginTop: 6 }}>{active.error}</p>
              )}
            </li>
          )}
          {(engine === "claude" || engine === "gemini") && (
            <li>
              Autorize, copie o código exibido e cole aqui.
              <span className="admin-user-meta"> Use o link desta tela - código de link antigo é recusado.</span>
              {finishing ? (
                <p className="admin-sub" style={{ marginTop: 6 }}>Validando o código…</p>
              ) : (
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="cole o código de autorização"
                    className="engine-input"
                    style={{ flex: "1 1 260px", minWidth: 0 }}
                  />
                  <button className="btn-approve" type="button" disabled={busy !== null || !code.trim()} onClick={submitCode}>
                    Enviar código
                  </button>
                </div>
              )}
              {active.status === "awaiting-code" && active.error && (
                <p className="auth-error" style={{ marginTop: 6 }}>{active.error}</p>
              )}
            </li>
          )}
        </ol>
        <button className="btn-reject" type="button" onClick={cancel}>
          Cancelar
        </button>
        {active.output && (
          <details style={{ marginTop: 8 }}>
            <summary className="admin-user-meta" style={{ cursor: "pointer" }}>Detalhes técnicos (saída do CLI)</summary>
            <pre style={{ fontSize: 11, padding: "6px 8px", overflowX: "auto", background: "var(--bg-elevated)", borderRadius: 6, maxHeight: 180 }}>
              {active.output.slice(-800)}
            </pre>
          </details>
        )}
      </div>
    );
  };

  const renderEngine = (engine: Engine) => {
    const unavailable =
      (engine === "codex" && !state.codexCliInstalled) || (engine === "gemini" && state.geminiCliInstalled === false);
    const profiles = state.profiles[engine] ?? [];
    const rotation = state.rotation?.[engine] ?? { strategy: "balanced" as Strategy, activeProfileId: null };

    return (
      <div className="admin-block" key={engine}>
        <h2>{LABEL[engine]}</h2>
        {unavailable && <p className="admin-sub">CLI {engine === "gemini" ? "do Gemini" : "do Codex"} indisponível neste container.</p>}
        {engine === "gemini" && (
          <p className="admin-sub">
            O Google <strong>desativou o login OAuth individual</strong> do Gemini CLI (out/2026, mensagem
            oficial aponta pro Antigravity). O caminho suportado é a <strong>chave de API do Google AI
            Studio</strong> (aistudio.google.com → Get API key) - cole abaixo na conta. A cobrança/limites
            passam a ser os da chave.
          </p>
        )}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <label className="admin-user-meta" htmlFor={`strategy-${engine}`} style={{ fontWeight: 600 }}>
            Estratégia de uso:
          </label>
          <select
            id={`strategy-${engine}`}
            className="engine-input"
            value={rotation.strategy}
            disabled={busy !== null}
            onChange={(e) =>
              void call(`/api/admin/engine-auth/${engine}/strategy`, {
                method: "PUT",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({ strategy: e.target.value, activeProfileId: rotation.activeProfileId }),
              })
            }
          >
            {(Object.keys(STRATEGY_LABEL) as Strategy[]).map((k) => (
              <option key={k} value={k}>{STRATEGY_LABEL[k]}</option>
            ))}
          </select>
        </div>

        <div className="engine-accounts">
          {profiles.map((p, idx) => (
            <div key={p.id} className="engine-account">
              <div className="engine-account-head">
                <span className="engine-account-name">
                  {p.label}
                  {idx === 0 && <span className="badge-admin">principal</span>}
                  {rotation.strategy === "manual" && rotation.activeProfileId === p.id && (
                    <span className="badge-admin" style={{ background: "var(--accent)", color: "#fff" }}>ativa</span>
                  )}
                </span>
                <span className={`engine-badge ${p.authed && !p.cooling ? "ok" : "off"}`}>
                  {p.cooling ? "Em espera" : p.authed ? "Autenticada" : "Não autenticada"}
                </span>
              </div>
              <div className="engine-account-stats">
                <span title="Respostas geradas por esta conta neste mês (registradas por este sistema)">
                  <strong>{p.usage?.responses ?? 0}</strong> respostas no mês
                </span>
                <span title="Tokens de entrada + saída no mês">
                  <strong>{fmtTok((p.usage?.inputTokens ?? 0) + (p.usage?.outputTokens ?? 0))}</strong> tokens
                </span>
                <span title="Custo estimado no mês (aproximação do provedor)">
                  <strong>${(p.usage?.costUsd ?? 0).toFixed(2)}</strong> estimado
                </span>
                {p.cooling && p.cooldownUntil && (
                  <span className="engine-cooldown" title={p.cooldownReason ?? ""}>
                    limite atingido - volta às {fmtHour(p.cooldownUntil)}
                  </span>
                )}
              </div>
              {p.cooling && p.cooldownReason && (
                <div className="admin-user-meta" title={p.cooldownReason}>motivo: {p.cooldownReason.slice(0, 110)}</div>
              )}
              {p.authed && state.liveUsage?.[p.id] && (
                <div className="engine-usage">
                  <UsageBar label="Sessão (5h)" pct={state.liveUsage[p.id]!.fiveHourPct} resetsAt={state.liveUsage[p.id]!.fiveHourResetsAt} />
                  <UsageBar label="Semana" pct={state.liveUsage[p.id]!.sevenDayPct} resetsAt={state.liveUsage[p.id]!.sevenDayResetsAt} />
                </div>
              )}
              <div className="engine-account-actions">
                {engine === "gemini" ? (
                  <>
                    <input
                      type="password"
                      className="engine-input"
                      placeholder={p.authed ? "nova chave (substitui a atual)" : "chave de API (Google AI Studio)"}
                      value={geminiKey[p.id] ?? ""}
                      autoComplete="new-password"
                      spellCheck={false}
                      style={{ flex: "1 1 220px", minWidth: 0 }}
                      onChange={(e) => setGeminiKey((prev) => ({ ...prev, [p.id]: e.target.value }))}
                    />
                    <button
                      className="btn-approve"
                      type="button"
                      disabled={busy !== null || !(geminiKey[p.id] ?? "").trim()}
                      onClick={() =>
                        void call(`/api/admin/engine-auth/gemini/profiles/${encodeURIComponent(p.id)}/apikey`, {
                          method: "POST",
                          headers: { "content-type": "application/json" },
                          body: JSON.stringify({ apiKey: (geminiKey[p.id] ?? "").trim() }),
                        }).then(() => setGeminiKey((prev) => ({ ...prev, [p.id]: "" })))
                      }
                    >
                      {p.authed ? "Substituir chave" : "Definir chave"}
                    </button>
                  </>
                ) : (
                <button
                  className="btn-approve"
                  type="button"
                  disabled={busy !== null || unavailable}
                  onClick={() => void start(engine, p.id)}
                >
                  {p.authed ? "Reautenticar" : "Autenticar"}
                </button>
                )}
                {p.cooling && (
                  <button
                    className="btn-reject"
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void call(`/api/admin/engine-auth/${engine}/profiles/${encodeURIComponent(p.id)}/clear-cooldown`)}
                  >
                    Liberar
                  </button>
                )}
                {p.authed && (
                  <button
                    className="btn-reject"
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void call(`/api/admin/engine-auth/${engine}/logout?profile=${encodeURIComponent(p.id)}`)}
                  >
                    Desconectar
                  </button>
                )}
                {idx !== 0 && (
                  <button
                    className="btn-reject"
                    type="button"
                    title="Passa esta conta pro topo da ordem de prioridade"
                    disabled={busy !== null}
                    onClick={() => void call(`/api/admin/engine-auth/${engine}/profiles/${encodeURIComponent(p.id)}/primary`)}
                  >
                    Tornar principal
                  </button>
                )}
                {rotation.strategy === "manual" && rotation.activeProfileId !== p.id && p.authed && (
                  <button
                    className="btn-reject"
                    type="button"
                    disabled={busy !== null}
                    onClick={() =>
                      void call(`/api/admin/engine-auth/${engine}/strategy`, {
                        method: "PUT",
                        headers: { "content-type": "application/json" },
                        body: JSON.stringify({ strategy: "manual", activeProfileId: p.id }),
                      })
                    }
                  >
                    Usar esta conta
                  </button>
                )}
                {p.id !== "default" && (
                  <button
                    className="btn-reject"
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void call(`/api/admin/engine-auth/${engine}/profiles/${encodeURIComponent(p.id)}`, { method: "DELETE" })}
                  >
                    Remover
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <input
            value={newLabel[engine]}
            onChange={(e) => setNewLabel((prev) => ({ ...prev, [engine]: e.target.value }))}
            placeholder="nome da nova conta (ex.: Conta reserva)"
            className="engine-input"
            style={{ flex: "1 1 220px", minWidth: 0 }}
          />
          <button className="btn-approve" type="button" disabled={busy !== null || !newLabel[engine].trim() || unavailable} onClick={() => void addAccount(engine)}>
            Adicionar conta
          </button>
        </div>

        {renderFlow(engine)}

        {active?.engine === engine && active.status === "error" && (
          <p className="auth-error" style={{ marginTop: 8 }}>
            Falhou ({profileLabel(engine, active.profileId)}): {active.error || "erro desconhecido"}
          </p>
        )}
        {active?.engine === engine && active.status === "done" && (
          <p className="engine-ok" style={{ marginTop: 8 }}>
            Conta "{profileLabel(engine, active.profileId)}" autenticada com sucesso.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="engines-panel">
      <section>
        <p className="admin-sub">
          Claude e Codex autenticam por OAuth (assinatura, sem API key); o Gemini usa chave de API do Google
          AI Studio (o Google desativou o OAuth individual do CLI). Conecte VÁRIAS contas por motor e escolha
          a estratégia: balanceado (rodízio), uma após a outra (só troca quando os tokens acabam) ou manual.
          Conta que atinge o limite entra em espera e o sistema alterna sozinho.
        </p>
        {msg && <p className="auth-error">{msg}</p>}
        {renderEngine("claude")}
        {renderEngine("codex")}
        {renderEngine("gemini")}
      </section>
    </div>
  );
}
